import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Paperclip, Trash, DownloadSimple, SpinnerGap, UploadSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_LABELS_AR = [
  'عقد عمل عن بُعد',
  'اتفاقية استشارة',
  'اتفاقية تعاون',
  'بطاقة الهوية / الإقامة',
  'شهادة سعودة',
  'مرفق آخر',
];
const PRESET_LABELS_EN = [
  'Remote Work Contract',
  'Consultancy Agreement',
  'Cooperation Agreement',
  'National ID / Iqama',
  'Saudization Certificate',
  'Other Attachment',
];

export default function EmployeeDocumentsDialog({ open, onOpenChange, employee, language, direction }) {
  const isAr = language === 'ar';
  const presets = isAr ? PRESET_LABELS_AR : PRESET_LABELS_EN;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState(presets[0]);
  const [customLabel, setCustomLabel] = useState('');
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const fetchDocs = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/employees/${employee.id}/documents`, { withCredentials: true });
      setDocs(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && employee?.id) fetchDocs();
    if (!open) {
      setFile(null);
      setLabel(presets[0]);
      setCustomLabel('');
      if (fileRef.current) fileRef.current.value = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee?.id]);

  const handleUpload = async () => {
    if (!file) {
      toast.error(isAr ? 'اختر ملفاً أولاً' : 'Pick a file first');
      return;
    }
    const finalLabel = label === '__custom' ? customLabel.trim() : label;
    if (!finalLabel) {
      toast.error(isAr ? 'أدخل وصفاً للملف' : 'Enter a label for the file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', finalLabel);
      await axios.post(`${API}/employees/${employee.id}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(isAr ? 'تم رفع الملف بنجاح' : 'File uploaded');
      setFile(null);
      setCustomLabel('');
      if (fileRef.current) fileRef.current.value = '';
      fetchDocs();
    } catch (e) {
      console.error(e);
      toast.error(isAr ? 'فشل رفع الملف' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(isAr ? 'حذف هذا الملف؟' : 'Delete this file?')) return;
    try {
      await axios.delete(`${API}/employees/${employee.id}/documents/${docId}`, { withCredentials: true });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      fetchDocs();
    } catch (e) {
      console.error(e);
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/employees/${employee.id}/documents/${doc.id}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name || doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(isAr ? 'فشل التحميل' : 'Download failed');
    }
  };

  const fmtSize = (b) => {
    if (!b) return '-';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[88vh] flex flex-col" dir={direction} data-testid="employee-docs-dialog">
        <div dir={direction} style={{ direction }} className="flex flex-col flex-1 min-h-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
              <Paperclip size={22} weight="duotone" />
              {isAr ? 'مستندات الموظف' : 'Employee Documents'} — {employee?.name}
            </DialogTitle>
            <DialogDescription>
              {isAr
                ? 'مرفقات داعمة (عقد عن بُعد، اتفاقية استشارة، شهادات سعودة...). تُستخدم للتوثيق وعمليات التدقيق.'
                : 'Supporting attachments (remote contract, consultancy agreement, Saudization certificates…) — used for audit and compliance.'}
            </DialogDescription>
          </DialogHeader>

          {/* Upload form */}
          <div className="mt-3 p-3 rounded-lg bg-[#F2F0FA] border border-[#C4BFF0] space-y-2">
            <p className="text-xs font-semibold text-[#655dc6]">
              {isAr ? 'إضافة مرفق جديد' : 'Add a new attachment'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-start block">{isAr ? 'نوع المرفق' : 'Attachment type'}</Label>
                <Select value={label} onValueChange={setLabel} dir={direction}>
                  <SelectTrigger className="bg-white" data-testid="doc-label-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {presets.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    <SelectItem value="__custom">{isAr ? 'وصف مخصص…' : 'Custom label…'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-start block">{isAr ? 'الملف' : 'File'}</Label>
                <Input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="bg-white text-xs"
                  data-testid="doc-file-input"
                />
              </div>
            </div>
            {label === '__custom' && (
              <Input
                placeholder={isAr ? 'اكتب وصف المرفق...' : 'Type the attachment description...'}
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                className="bg-white text-xs"
                data-testid="doc-custom-label"
              />
            )}
            <div className="flex justify-end pt-1">
              <Button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                data-testid="doc-upload-btn"
              >
                {uploading ? <SpinnerGap size={16} className="animate-spin" /> : <UploadSimple size={16} className="me-1" />}
                {isAr ? 'رفع' : 'Upload'}
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="mt-3 flex-1 min-h-0 overflow-hidden">
            <p className="text-xs font-semibold text-[#655dc6] mb-2">
              {isAr ? `المرفقات الحالية (${docs.length})` : `Current attachments (${docs.length})`}
            </p>
            <ScrollArea className="h-[260px] pe-2">
              {loading ? (
                <div className="flex justify-center py-6 text-[#655dc6]/70">
                  <SpinnerGap size={22} className="animate-spin" />
                </div>
              ) : docs.length === 0 ? (
                <div className="text-center py-6 text-[#655dc6]/60 text-sm">
                  {isAr ? 'لا توجد مرفقات بعد' : 'No attachments yet'}
                </div>
              ) : (
                <ul className="space-y-2">
                  {docs.map((d) => (
                    <li key={d.id}
                        className="flex items-center justify-between gap-2 p-2 rounded-md bg-white border border-[#C4BFF0]"
                        data-testid={`doc-row-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#655dc6] truncate">{d.label}</p>
                        <p className="text-[10px] text-[#655dc6]/60 truncate">
                          {d.original_name} · {fmtSize(d.size)} · {d.uploaded_at?.slice(0, 10)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#655dc6]/70 hover:text-[#655dc6]"
                                onClick={() => handleDownload(d)} data-testid={`doc-download-${d.id}`}>
                          <DownloadSimple size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[#655dc6]/70 hover:text-red-600"
                                onClick={() => handleDelete(d.id)} data-testid={`doc-delete-${d.id}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
